import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
// import { GetUserApplicationsByUserId } from 'services/security/endpoints/UserApplicationService';
// import { UserApplicationResponse } from 'services/security/interfaces/UserApplicationInterface';
import { ApplicationResponse } from 'services/security/interfaces/ApplicationInterface';
import { ChangeUserLanguage } from 'services/security/endpoints/UserProfileService';
import { GetApplications } from 'services/security/endpoints/ApplicationService';
import LogoIconShort from 'styles/images/Logo_corto.svg';
import { GetToken, GetUser, Logout } from 'constants/Global';
import EmptyProfile from 'styles/images/ProfileEmpty.jpg';
import LogoIcon from 'styles/images/Logo_completo.svg';
import { LayoutContext } from './context/layoutcontext';
import { OverlayPanel } from 'primereact/overlaypanel';
import ApplicationSideBar from './ApplicationSideBar'
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { StyleClass } from 'primereact/styleclass';
import { InputText } from 'primereact/inputtext';
import type { AppTopbarRef } from 'types/layout';
import { changeLanguage } from '../locale/i18n';
import CompanySelector from './CompanySelector';
import { useTranslation } from 'react-i18next';
import { IMAGE_VISOR } from 'services/Paths';
import { Ripple } from 'primereact/ripple';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { GetUserApplicationsByUserId } from 'services/security/endpoints/UserApplicationService';
import { UserApplicationResponse } from 'services/security/interfaces/UserApplicationInterface';

const AppTopbar = forwardRef<AppTopbarRef>((props, ref) => {
    const navigate = useNavigate();
    const { t } = useTranslation(['common']);
    const { onMenuToggle, onTopbarMenuToggle, layoutConfig, layoutState } = useContext(LayoutContext);
    const user = GetUser();
    const menubuttonRef = useRef(null);
    const searchInputRef = useRef(null);
    const mobileButtonRef = useRef(null);
    const closeBtnRef = useRef(null);
    const [image, setImage] = useState<string>(EmptyProfile);
    const languageOverlayRef = useRef<OverlayPanel>(null);
    const userMenuOverlayRef = useRef<OverlayPanel>(null);
    const notificationOverlayRef = useRef<OverlayPanel>(null);
    const externalAppsOverlayRef = useRef<OverlayPanel>(null);
    const [currentLang, setCurrentLang] = useState<string>(localStorage.getItem('i18nextLng') || 'es');
    const [applicationSideBarVisible, setApplicationSideBarVisible] = useState(false);
    const [externalApps, setExternalApps] = useState<ApplicationResponse[]>([]);

    useEffect(() => {
        if (user && user.idUser) {
            setImage(user?.imageDisplayId ? `${IMAGE_VISOR}/${user.imageDisplayId}` : EmptyProfile);
        }
    }, [user]);

    useEffect(() => {
        const fetchExternalAppsByUser = async () => {
            if (isPublicRoute) return;
            try {
                if (user && user.idUser) {
                    const [userAppsResponse, appsResponse] = await Promise.all([GetUserApplicationsByUserId(user.idUser, { Status: 'ACTIVO', PageSize: 100, IncludeApplication: false }), GetApplications({ status: 'ACTIVO', PageSize: 100 })]);
                    const appsUser = userAppsResponse.data.data as UserApplicationResponse[];
                    const apps = appsResponse.data.data as ApplicationResponse[];
                    const externalApps = apps.filter((app) => app.isExternalApplication && appsUser.some((ua) => ua.applicationId === app.idApplication));
                    setExternalApps(externalApps);
                }
            } catch (error) {
                console.error('Error fetching external applications for user:', error);
            }
        };
        fetchExternalAppsByUser();
    }, []);

    const onMenuButtonClick = () => {
        onMenuToggle();
    };

    const onMobileTopbarMenuButtonClick = () => {
        onTopbarMenuToggle();
    };

    const handleLanguageChange = async (lang: string) => {
        const success = await changeLanguage(lang);
        if (success) {
            setCurrentLang(lang);
            languageOverlayRef.current?.hide();

            if (user && user.idUser) {
                try {
                    await ChangeUserLanguage(user.idUser, { lang });
                } catch (error) {
                    console.error('Error al guardar el idioma en el servidor:', error);
                }
            }
        }
    };

    useImperativeHandle(ref, () => ({
        menubutton: menubuttonRef.current
    }));

    const shouldShowLogoText = () => {
        if (layoutConfig.menuMode === 'slim' || layoutConfig.menuMode === 'slim-plus') {
            return false;
        }

        if (layoutConfig.menuMode === 'drawer' || layoutConfig.menuMode === 'reveal') {
            return layoutState.sidebarActive;
        }

        return true;
    };

    const location = useLocation();
    const isPublicRoute = location.pathname.startsWith('/customs/pre-liquidation/approve/')
                          || location.pathname.startsWith('/customs/inspection/photos/')
                          || location.pathname.startsWith('/customs/control/photos/')
                          || location.pathname.startsWith('/customs/orders/')  ;

    return (
        <div className="layout-topbar">
            <div className="layout-topbar-start">
                <Link className="layout-topbar-logo" to="/">
                    {shouldShowLogoText() && <img src={LogoIcon} style={{ width: 220, height: 60 }} alt="Logo" />}
                    {!shouldShowLogoText() && <img src={LogoIconShort} style={{ width: 50, height: 50 }} alt="Logo" />}
                </Link>
                {!isPublicRoute && (
                    <a ref={menubuttonRef} className="p-ripple layout-menu-button" onClick={onMenuButtonClick}>
                        <i className="pi pi-chevron-right"></i>
                        <Ripple />
                    </a>
                )}

                {!isPublicRoute && (
                    <a ref={mobileButtonRef} className="p-ripple layout-topbar-mobile-button" onClick={onMobileTopbarMenuButtonClick}>
                        <i className="pi pi-ellipsis-v"></i>
                        <Ripple />
                    </a>
                )}
            </div>
            <div className="layout-topbar-end">
                <div className="layout-topbar-actions-start">
                    {!isPublicRoute && externalApps.length > 0 && (
                        <li className="flex align-items-center">
                            <Button className="p-button-text p-button-rounded flex align-items-center" onClick={(e) => externalAppsOverlayRef.current?.toggle(e)} style={{ fontWeight: 'bold', height: '3rem', color: 'white' }}>
                                {t('externalApps', 'Aplicaciones Externas')}
                                <i className="pi pi-chevron-down ml-2"></i>
                            </Button>
                            <OverlayPanel ref={externalAppsOverlayRef} style={{ width: '220px' }}>
                                {externalApps.map((app) => (
                                    <div key={app.idApplication} className="p-2 cursor-pointer hover:surface-200 border-round flex align-items-center" onClick={() => window.open(`${app.urlToRedirect}?token=${GetToken()}`, '_blank')}>
                                        <i className={`${app.icon || 'pi pi-external-link'} mr-2`}></i>
                                        <span>{app.name}</span>
                                    </div>
                                ))}
                            </OverlayPanel>
                        </li>
                    )}
                </div>
                <div className="layout-topbar-actions-end">
                    <ul className="layout-topbar-items">
                        {!isPublicRoute && (
                            <li className="layout-topbar-search">
                                <div className="layout-search-panel hidden p-input-filled">
                                    <i className="pi pi-search"></i>
                                    <InputText ref={searchInputRef} placeholder={t('common:search')} />
                                    <StyleClass nodeRef={closeBtnRef} selector=".layout-search-panel" leaveActiveClassName="fadeout" leaveToClassName="hidden">
                                        <Button ref={closeBtnRef} type="button" icon="pi pi-times" rounded text className="p-button-plain"></Button>
                                    </StyleClass>
                                </div>
                            </li>
                        )}
                        {!isPublicRoute && (
                            <li className="flex align-items-center">
                                <CompanySelector />
                            </li>
                        )}
                        <li className="flex align-items-center">
                            <Button className="p-button-text p-button-rounded flex align-items-center" onClick={(e) => languageOverlayRef.current?.toggle(e)} style={{ fontWeight: 'bold', color: 'white', height: '3rem' }}>
                                <span className={`fp fp-md ${currentLang === 'en' ? 'us' : 'ec'} mr-2`}></span>
                                {(currentLang || 'es').toUpperCase()}
                                <i className="pi pi-chevron-down ml-2"></i>
                            </Button>

                            <OverlayPanel ref={languageOverlayRef} style={{ width: '150px' }}>
                                <div className="p-2">
                                    <div key="lang-es" className={`p-2 cursor-pointer hover:surface-200 border-round flex align-items-center ${currentLang === 'es' ? 'bg-primary-100' : ''}`} onClick={() => handleLanguageChange('es')}>
                                        <span className="font-bold">Español</span>
                                        {currentLang === 'es' && <i className="pi pi-check ml-auto text-primary"></i>}
                                    </div>
                                    <div key="lang-en" className={`p-2 cursor-pointer hover:surface-200 border-round flex align-items-center ${currentLang === 'en' ? 'bg-primary-100' : ''}`} onClick={() => handleLanguageChange('en')}>
                                        <span className="font-bold">English</span>
                                        {currentLang === 'en' && <i className="pi pi-check ml-auto text-primary"></i>}
                                    </div>
                                </div>
                            </OverlayPanel>
                        </li>
                        {!isPublicRoute && (
                            <>
                                <li className="flex align-items-center">
                                    <Button
                                        className="p-button-text p-button-rounded p-button-plain flex align-items-center"
                                        icon="pi pi-bell"
                                        style={{ height: '3rem', color: 'white' }}
                                        onClick={(e) => notificationOverlayRef.current?.toggle(e)}
                                        aria-label="Notificaciones"
                                    />
                                    <OverlayPanel ref={notificationOverlayRef} style={{ width: '220px' }}>
                                        <div className="p-3 text-center text-500">
                                            <i className="pi pi-bell" style={{ fontSize: '2rem', color: '#f97316' }}></i>
                                            <div className="mt-2 font-semibold text-lg">No hay notificaciones</div>
                                            <small className="text-400">Estás al día por ahora</small>
                                        </div>
                                    </OverlayPanel>
                                </li>
                                <li className="flex align-items-center">
                                    <Button
                                        className="p-button-text p-button-rounded p-button-plain flex align-items-center"
                                        onClick={(e) => userMenuOverlayRef.current?.toggle(e)}
                                        aria-label={t('common:userAvatar', 'Avatar de usuario')}
                                        style={{ height: '3rem' }}
                                    >
                                        <Avatar image={image} shape="circle" style={{ width: '3rem', height: '3rem' }} />
                                    </Button>
                                    <OverlayPanel ref={userMenuOverlayRef} style={{ width: '200px' }}>
                                        <div className="p-2">
                                            <div
                                                className="p-2 cursor-pointer hover:surface-200 border-round flex align-items-center"
                                                onClick={() => {
                                                    navigate('/profile');
                                                    userMenuOverlayRef.current?.hide();
                                                }}
                                            >
                                                <i className="pi pi-user mr-2"></i>
                                                <span>{t('common:myProfile', 'Mi Perfil')}</span>
                                            </div>
                                            <div
                                                className="p-2 cursor-pointer hover:surface-200 border-round flex align-items-center"
                                                onClick={() => {
                                                    Logout();
                                                    navigate('/login');
                                                    userMenuOverlayRef.current?.hide();
                                                }}
                                            >
                                                <i className="pi pi-sign-out mr-2"></i>
                                                <span>{t('common:logout', 'Cerrar Sesión')}</span>
                                            </div>
                                        </div>
                                    </OverlayPanel>
                                </li>
                                <li className="flex align-items-center">
                                    <Button className="p-button-text p-button-rounded" style={{ fontWeight: 'bold', color: 'white' }} onClick={() => setApplicationSideBarVisible(true)} icon="pi pi-arrow-left" />
                                </li>
                            </>
                        )}
                    </ul>
                </div>
            </div>
            <ApplicationSideBar visible={applicationSideBarVisible} setVisible={setApplicationSideBarVisible} />
        </div>
    );
});

AppTopbar.displayName = 'AppTopbar';

export default AppTopbar;